import React, { useState, useCallback, useMemo, useRef, useEffect, CSSProperties } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
    DndContext, DragEndEvent, DragOverEvent, DragStartEvent,
    PointerSensor, TouchSensor, useSensor, useSensors, useDroppable, closestCenter,
} from '@dnd-kit/core';
import { useKanban } from '../context/KanbanContext';
import { TarjetaTarea } from './TarjetaTarea';
import { toast } from '../lib/toast';
import { ModalEditarTarea } from './ModalEditarTarea';
import { TareaConRelaciones, COLUMNS_ORDER, ESTADO_LABELS, FASE_LABELS, EstadoTarea } from '../types';
import { TableroSkeleton } from './TableroSkeleton';
import { EmptyState } from './EmptyState';

const columnHeaderColors: Record<EstadoTarea, string> = {
    backlog: 'from-slate-500 to-slate-600',
    por_hacer: 'from-blue-500 to-blue-600',
    en_progreso: 'from-amber-500 to-amber-600',
    esperando_cliente: 'from-orange-500 to-orange-600',
    testing: 'from-purple-500 to-purple-600',
    produccion: 'from-emerald-500 to-emerald-600'
};

const columnIcons: Record<EstadoTarea, string> = {
    backlog: '📋',
    por_hacer: '🎯',
    en_progreso: '⚙️',
    esperando_cliente: '⏳',
    testing: '🧪',
    produccion: '✅'
};

const emptyTareasPorEstado = (): Record<EstadoTarea, TareaConRelaciones[]> => ({
    backlog: [],
    por_hacer: [],
    en_progreso: [],
    esperando_cliente: [],
    testing: [],
    produccion: []
});

// Id único de cada columna droppable = `<clave-de-swimlane>::<estado>`. Se
// arma/desarma con estas dos funciones para no repetir el separador en
// varios lugares.
const dropIdDe = (swimlaneClave: string, estado: EstadoTarea) => `${swimlaneClave}::${estado}`;
const estadoDeDropId = (dropId: string): EstadoTarea => dropId.split('::').pop() as EstadoTarea;

interface PhaseCollapsibleProps {
    fase: string;
    faseLabel: string;
    color: string;
    tareas: Record<EstadoTarea, TareaConRelaciones[]>;
    expanded: boolean;
    onToggle: () => void;
    onTareaClick: (tarea: TareaConRelaciones) => void;
}

const PhaseCollapsible: React.FC<PhaseCollapsibleProps> = ({
    fase, faseLabel, color, tareas, expanded, onToggle, onTareaClick
}) => {
    const totalTareas = Object.values(tareas).reduce((acc, arr) => acc + arr.length, 0);
    const completadas = tareas['produccion']?.length || 0;
    const enProgreso = (tareas['en_progreso']?.length || 0) + (tareas['esperando_cliente']?.length || 0);

    return (
        <div className="mb-4 border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <button
                onClick={onToggle}
                className="w-full flex items-center justify-between px-4 py-3 text-white font-medium transition-all hover:opacity-90"
                style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}
            >
                <div className="flex items-center gap-3">
                    <svg
                        className={`w-5 h-5 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="font-semibold">{faseLabel}</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-white/60"></span>
                        {totalTareas} total
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                        {enProgreso}
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                        {completadas}
                    </span>
                </div>
            </button>

            {expanded && (
                <div className="p-4 bg-slate-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                        {COLUMNS_ORDER.map(estado => (
                            <div
                                key={estado}
                                className="bg-white rounded-lg overflow-hidden border border-slate-200"
                            >
                                <div className={`${columnHeaderColors[estado]} text-white px-3 py-2`}>
                                    <div className="flex items-center justify-between">
                                        <span className="font-semibold text-sm">{ESTADO_LABELS[estado]}</span>
                                        <span className="bg-white/20 px-2 py-0.5 rounded text-xs">{tareas[estado].length}</span>
                                    </div>
                                </div>
                                <div className="p-2 space-y-2 max-h-[45vh] overflow-y-auto">
                                    {tareas[estado].length === 0 ? (
                                        <p className="text-slate-400 text-xs text-center py-4">Sin tareas</p>
                                    ) : (
                                        tareas[estado].map(tarea => (
                                            <div
                                                key={tarea.id}
                                                onClick={() => onTareaClick(tarea)}
                                                className="cursor-pointer"
                                            >
                                                {/* Vista de solo lectura: sin drag, es un resumen por fase. */}
                                                <TarjetaTarea tarea={tarea} draggable={false} onClick={() => {}} />
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

interface ColumnProps {
    estado: EstadoTarea;
    dropId: string;
    tareas: TareaConRelaciones[];
    isDragOver: boolean;
    onTareaClick: (tarea: TareaConRelaciones) => void;
    limite?: number | null;
    globalCount: number;
}

const Column: React.FC<ColumnProps> = ({ estado, dropId, tareas, isDragOver, onTareaClick, limite, globalCount }) => {
    const { setNodeRef } = useDroppable({ id: dropId });

    const tieneLimite = limite !== null && limite !== undefined;
    const globalLimit = limite as number;
    const wipPct = tieneLimite ? Math.min(100, (globalCount / globalLimit) * 100) : 0;
    const wipExcedido = tieneLimite && globalCount > globalLimit;
    const wipEnLimite = tieneLimite && globalCount === globalLimit;
    const wipCerca = tieneLimite && !wipExcedido && globalCount / globalLimit >= 0.8;

    const wipBarColor = wipExcedido
        ? 'bg-red-400'
        : wipEnLimite
            ? 'bg-amber-300'
            : wipCerca
                ? 'bg-yellow-200'
                : 'bg-white/40';

    const iconContainer = wipExcedido
        ? 'bg-red-500/30 ring-2 ring-red-300'
        : wipEnLimite
            ? 'bg-amber-400/30 ring-2 ring-amber-300'
            : 'bg-white/20';

    return (
        <motion.div
            ref={setNodeRef}
            layout
            animate={isDragOver ? { scale: 1.015 } : { scale: 1 }}
            transition={{ type: 'spring', stiffness: 400, damping: 28 }}
            className={`
                flex flex-col bg-gradient-to-b from-slate-50 to-slate-100/80 backdrop-blur rounded-xl
                min-h-[220px] w-[88vw] max-w-[400px] flex-shrink-0 snap-center
                sm:w-0 sm:flex-1 sm:min-w-[260px] sm:max-w-[420px] sm:snap-none
                transition-colors duration-200 border border-slate-200/60
                shadow-sm hover:shadow-md
                ${isDragOver ? 'ring-2 ring-blue-400 ring-dashed bg-blue-50/80' : ''}
            `}
        >
            <div className={`bg-gradient-to-br ${columnHeaderColors[estado]} text-white px-3 py-3 rounded-t-xl relative overflow-hidden`}>
                <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top_right,white,transparent_60%)]" />
                <div className="flex items-center justify-between gap-2 relative">
                    <div className="flex items-center gap-2 min-w-0">
                        <span className={`w-7 h-7 rounded-lg ${iconContainer} flex items-center justify-center text-base flex-shrink-0 transition-all`}>
                            {columnIcons[estado]}
                        </span>
                        <span className="font-bold text-xs uppercase tracking-wider truncate">
                            {ESTADO_LABELS[estado]}
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="bg-white/25 backdrop-blur-sm px-2.5 py-1 rounded-md text-xs font-bold min-w-[28px] text-center">
                            {tareas.length}
                        </span>
                    </div>
                </div>

                {tieneLimite && (
                    <div className="mt-2.5 relative">
                        <div className="flex items-center justify-between text-[10px] font-semibold mb-1">
                            <span className="opacity-90">WIP</span>
                            <span className={`font-mono ${wipExcedido ? 'text-red-100' : wipEnLimite ? 'text-amber-100' : 'opacity-90'}`}>
                                {globalCount}/{globalLimit}
                            </span>
                        </div>
                        <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
                            <div
                                className={`h-full ${wipBarColor} rounded-full transition-all duration-500`}
                                style={{ width: `${wipPct}%` }}
                            />
                        </div>
                    </div>
                )}
            </div>
            <div className="flex-1 p-2 overflow-y-auto max-h-[65vh] scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent">
                {tareas.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-32 text-slate-400 px-3 py-6 border-2 border-dashed border-slate-200 rounded-lg m-1">
                        <span className="text-3xl mb-1 opacity-40">{columnIcons[estado]}</span>
                        <span className="text-xs font-medium">Sin tareas</span>
                        <span className="text-[10px] text-slate-300 mt-0.5">arrastrá una tarjeta acá</span>
                    </div>
                ) : (
                    <AnimatePresence mode="popLayout">
                        {tareas.map((tarea, idx) => (
                            <TarjetaTarea
                                key={tarea.id}
                                tarea={tarea}
                                index={idx}
                                onClick={onTareaClick}
                            />
                        ))}
                    </AnimatePresence>
                )}
            </div>
        </motion.div>
    );
};

interface SwimlaneProps {
    swimlaneClave: string;
    label: string;
    colorClassName?: string;
    colorStyle?: CSSProperties;
    tareas: Record<EstadoTarea, TareaConRelaciones[]>;
    overId: string | null;
    onTareaClick: (tarea: TareaConRelaciones) => void;
    globalCounts: Record<EstadoTarea, number>;
    limitesMap: Partial<Record<EstadoTarea, number | null>>;
}

const Swimlane: React.FC<SwimlaneProps> = ({ swimlaneClave, label, colorClassName, colorStyle, tareas, overId, onTareaClick, globalCounts, limitesMap }) => {
    const totalTareas = Object.values(tareas).reduce((acc, arr) => acc + arr.length, 0);
    const completadas = tareas['produccion']?.length || 0;
    const enProgreso = (tareas['en_progreso']?.length || 0) + (tareas['esperando_cliente']?.length || 0);

    // Indicador de scroll horizontal: en pantallas de ~1024-1280px las 6
    // columnas no entran (aunque se reparten el ancho, tienen un mínimo de
    // 260px) y hay que scrollear — pero la única pista era la scrollbar
    // fina, fácil de no notar. Este degradado + flecha en el borde derecho
    // avisa "hay más" y desaparece solo al llegar al final del scroll.
    const scrollRef = useRef<HTMLDivElement>(null);
    const [hayMasAScroll, setHayMasAScroll] = useState(false);

    const chequearOverflow = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setHayMasAScroll(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
    }, []);

    useEffect(() => {
        chequearOverflow();
        const el = scrollRef.current;
        if (!el) return;
        const resizeObserver = new ResizeObserver(chequearOverflow);
        resizeObserver.observe(el);
        window.addEventListener('resize', chequearOverflow);
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', chequearOverflow);
        };
    }, [chequearOverflow, totalTareas]);

    if (totalTareas === 0) return null;

    return (
        <div className="mb-6">
            <div className="flex items-center gap-4 mb-3">
                <div
                    className={`px-4 py-2 rounded-xl text-white font-semibold text-sm shadow-lg ${colorClassName || ''}`}
                    style={colorStyle}
                >
                    {label}
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                        {totalTareas} total
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                        {enProgreso} en progreso
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                        {completadas} completadas
                    </span>
                </div>
            </div>
            <div className="relative">
                <div
                    ref={scrollRef}
                    onScroll={chequearOverflow}
                    className="flex gap-3 overflow-x-auto pb-3 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent snap-x snap-mandatory sm:snap-none"
                >
                    {COLUMNS_ORDER.map(estado => {
                        const dropId = dropIdDe(swimlaneClave, estado);
                        return (
                            <Column
                                key={estado}
                                estado={estado}
                                dropId={dropId}
                                tareas={tareas[estado]}
                                isDragOver={overId === dropId}
                                onTareaClick={onTareaClick}
                                limite={limitesMap[estado]}
                                globalCount={globalCounts[estado]}
                            />
                        );
                    })}
                </div>
                <div
                    aria-hidden="true"
                    className={`pointer-events-none absolute top-0 right-0 bottom-3 w-16 flex items-center justify-end pr-1 bg-gradient-to-l from-slate-50 via-slate-50/80 to-transparent transition-opacity duration-200 ${
                        hayMasAScroll ? 'opacity-100' : 'opacity-0'
                    }`}
                >
                    <span className="w-6 h-6 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center text-slate-400 text-sm">
                        →
                    </span>
                </div>
            </div>
        </div>
    );
};

interface SwimlaneCombinada {
    clave: string;
    clienteId: number;
    clienteNombre: string;
    fase: string;
    faseLabel: string;
    color: string;
    tareas: Record<EstadoTarea, TareaConRelaciones[]>;
}

interface ClienteTab {
    id: number;
    nombre: string;
    color: string;
    totalTareas: number;
}

export const TableroKanban: React.FC = () => {
    const { swimlanes, loading, error, actualizarEstadoTarea, actualizarTarea, eliminarTarea, marcarCompletado, fetchKanbanBoard, wipLimites } = useKanban();
    const [overId, setOverId] = useState<string | null>(null);
    const [tareaSeleccionada, setTareaSeleccionada] = useState<TareaConRelaciones | null>(null);
    const [clienteSeleccionado, setClienteSeleccionado] = useState<number | null>(null);
    const [fasesExpandidas, setFasesExpandidas] = useState<Set<string>>(new Set());

    // Sensores dnd-kit: mouse (PointerSensor, con un pequeño umbral de
    // distancia para no interferir con el click normal de abrir la tarjeta)
    // y touch (TouchSensor, con delay+tolerance para distinguir "arrastrar"
    // de "hacer scroll de la página con el dedo") — esto es lo que hace que
    // el drag & drop funcione igual en celular que en desktop.
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    );

    const limitesMap = useMemo(() => {
        const map: Partial<Record<EstadoTarea, number | null>> = {};
        wipLimites.forEach(w => { map[w.estado] = w.limite; });
        return map;
    }, [wipLimites]);

    const globalCounts = useMemo(() => {
        const counts: Record<EstadoTarea, number> = {
            backlog: 0,
            por_hacer: 0,
            en_progreso: 0,
            esperando_cliente: 0,
            testing: 0,
            produccion: 0
        };
        swimlanes.forEach(sw => {
            COLUMNS_ORDER.forEach(estado => {
                counts[estado] += sw.tareas[estado]?.length || 0;
            });
        });
        return counts;
    }, [swimlanes]);

    // Mapa id de tarea → tarea, para resolver qué se soltó en `onDragEnd`
    // (dnd-kit solo nos da el id del draggable, no el objeto completo).
    const tareaPorId = useMemo(() => {
        const map = new Map<number, TareaConRelaciones>();
        swimlanes.forEach(sw => {
            COLUMNS_ORDER.forEach(estado => {
                sw.tareas[estado]?.forEach(tarea => map.set(tarea.id, tarea));
            });
        });
        return map;
    }, [swimlanes]);

    const swimlanesCombinadas = useMemo(() => {
        const groupsMap = new Map<string, SwimlaneCombinada>();

        swimlanes.forEach(sw => {
            COLUMNS_ORDER.forEach(estado => {
                sw.tareas[estado].forEach(tarea => {
                    const clave = `${tarea.cliente_id}-${sw.fase}`;
                    if (!groupsMap.has(clave)) {
                        groupsMap.set(clave, {
                            clave,
                            clienteId: tarea.cliente_id,
                            clienteNombre: tarea.empresa,
                            fase: sw.fase,
                            faseLabel: FASE_LABELS[sw.fase],
                            color: tarea.cliente_color,
                            tareas: emptyTareasPorEstado()
                        });
                    }
                    groupsMap.get(clave)!.tareas[estado].push(tarea);
                });
            });
        });

        return Array.from(groupsMap.values()).sort((a, b) => {
            const labelCompare = a.clienteNombre.localeCompare(b.clienteNombre);
            if (labelCompare !== 0) return labelCompare;
            return a.fase.localeCompare(b.fase);
        });
    }, [swimlanes]);

    const clientesTabs = useMemo((): ClienteTab[] => {
        const clienteMap = new Map<number, ClienteTab>();
        swimlanesCombinadas.forEach(sw => {
            const total = Object.values(sw.tareas).reduce((acc, arr) => acc + arr.length, 0);
            if (!clienteMap.has(sw.clienteId)) {
                clienteMap.set(sw.clienteId, {
                    id: sw.clienteId,
                    nombre: sw.clienteNombre,
                    color: sw.color,
                    totalTareas: total
                });
            } else {
                clienteMap.get(sw.clienteId)!.totalTareas += total;
            }
        });
        return Array.from(clienteMap.values()).sort((a, b) => a.nombre.localeCompare(b.nombre));
    }, [swimlanesCombinadas]);

    const swimlanesFiltradas = useMemo(() => {
        if (clienteSeleccionado === null) return swimlanesCombinadas;
        return swimlanesCombinadas.filter(sw => sw.clienteId === clienteSeleccionado);
    }, [swimlanesCombinadas, clienteSeleccionado]);

    const toggleFase = useCallback((fase: string) => {
        setFasesExpandidas(prev => {
            const newSet = new Set(prev);
            if (newSet.has(fase)) {
                newSet.delete(fase);
            } else {
                newSet.add(fase);
            }
            return newSet;
        });
    }, []);

    const handleTareaClick = useCallback((tarea: TareaConRelaciones) => {
        setTareaSeleccionada(tarea);
    }, []);

    const handleDragStart = useCallback((_event: DragStartEvent) => {
        setOverId(null);
    }, []);

    const handleDragOver = useCallback((event: DragOverEvent) => {
        setOverId(event.over ? String(event.over.id) : null);
    }, []);

    const handleDragCancel = useCallback(() => {
        setOverId(null);
    }, []);

    const handleDragEnd = useCallback(async (event: DragEndEvent) => {
        const { active, over } = event;
        setOverId(null);
        if (!over) return;

        const nuevoEstado = estadoDeDropId(String(over.id));
        const draggedTarea = tareaPorId.get(Number(active.id));
        if (!draggedTarea || draggedTarea.estado === nuevoEstado) return;

        const limite = limitesMap[nuevoEstado];
        const esExpedito = draggedTarea.clase_servicio === 'expedito';

        if (!esExpedito && limite !== null && limite !== undefined) {
            const proyectado = globalCounts[nuevoEstado] + 1;
            if (proyectado > limite) {
                const confirmar = await toast.confirm({
                    title: `⚠️ Límite WIP excedido`,
                    description: `La columna "${ESTADO_LABELS[nuevoEstado]}" tiene límite ${limite} y actualmente tiene ${globalCounts[nuevoEstado]} tarea(s). Mover esta la dejará en ${proyectado}.\n\n¿Continuar de todas formas?`,
                    confirmText: 'Mover igual',
                    type: 'warning'
                });
                if (!confirmar) return;
            }
        }

        await actualizarEstadoTarea(draggedTarea.id, nuevoEstado);
    }, [tareaPorId, actualizarEstadoTarea, limitesMap, globalCounts]);

    if (loading) {
        return <TableroSkeleton />;
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <p className="text-red-700 font-medium mb-4">{error}</p>
                <button
                    onClick={fetchKanbanBoard}
                    className="px-6 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium"
                >
                    Reintentar
                </button>
            </div>
        );
    }

    const hasAnyTasks = swimlanes.some(s => Object.values(s.tareas).some(arr => arr.length > 0));

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            {/* Cada Swimlane maneja su propio scroll horizontal — este wrapper ya
                no necesita overflow-x-auto (lo tenía antes, quedaba redundante
                y podía recortar sombras/hover de las tarjetas del borde). */}
            <div>
                {hasAnyTasks && clientesTabs.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        <button
                            onClick={() => setClienteSeleccionado(null)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                clienteSeleccionado === null
                                    ? 'bg-slate-700 text-white shadow-md'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                            Todos ({clientesTabs.reduce((acc, c) => acc + c.totalTareas, 0)})
                        </button>
                        {clientesTabs.map(cliente => (
                            <button
                                key={cliente.id}
                                onClick={() => setClienteSeleccionado(cliente.id)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                                    clienteSeleccionado === cliente.id
                                        ? 'ring-2 ring-offset-1 shadow-md'
                                        : 'hover:scale-105'
                                }`}
                                style={{
                                    backgroundColor: clienteSeleccionado === cliente.id ? cliente.color : `${cliente.color}20`,
                                    color: clienteSeleccionado === cliente.id ? 'white' : cliente.color,
                                    border: `1px solid ${cliente.color}40`
                                }}
                            >
                                <span
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: clienteSeleccionado === cliente.id ? 'white' : cliente.color }}
                                />
                                {cliente.nombre}
                                <span className={`px-1.5 py-0.5 rounded text-xs ${
                                    clienteSeleccionado === cliente.id ? 'bg-white/20' : 'bg-opacity-30'
                                }`}>
                                    {cliente.totalTareas}
                                </span>
                            </button>
                        ))}
                    </div>
                )}

                {!hasAnyTasks && (
                    <EmptyState
                        icon="📋"
                        title="No hay tareas en el tablero"
                        description='Comenzá agregando una nueva tarea usando el botón "Nueva Tarea" en la parte superior.'
                        action={{
                            label: 'Ver cómo empezar',
                            onClick: () => toast.info('Tip: usá el botón "+ Nueva Tarea" en el header para crear tu primera tarea')
                        }}
                    />
                )}

                {clienteSeleccionado !== null ? (
                    swimlanesFiltradas.length > 0 ? (
                        <div>
                            <h3 className="text-lg font-semibold text-slate-700 mb-4" style={{ color: swimlanesFiltradas[0].color }}>
                                {swimlanesFiltradas[0].clienteNombre}
                            </h3>
                            {swimlanesFiltradas.map(sw => (
                                <PhaseCollapsible
                                    key={sw.clave}
                                    fase={sw.fase}
                                    faseLabel={sw.faseLabel}
                                    color={sw.color}
                                    tareas={sw.tareas}
                                    expanded={fasesExpandidas.has(sw.clave)}
                                    onToggle={() => toggleFase(sw.clave)}
                                    onTareaClick={handleTareaClick}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-10 text-slate-500">
                            Este cliente no tiene tareas
                        </div>
                    )
                ) : (
                    swimlanesFiltradas.map(sw => (
                        <Swimlane
                            key={sw.clave}
                            swimlaneClave={sw.clave}
                            label={`${sw.clienteNombre} - ${sw.faseLabel}`}
                            colorStyle={{ backgroundColor: sw.color }}
                            tareas={sw.tareas}
                            overId={overId}
                            onTareaClick={handleTareaClick}
                            globalCounts={globalCounts}
                            limitesMap={limitesMap}
                        />
                    ))
                )}

                {tareaSeleccionada && (
                    <ModalEditarTarea
                        tarea={tareaSeleccionada}
                        onClose={() => setTareaSeleccionada(null)}
                        onActualizar={actualizarTarea}
                        onEliminar={eliminarTarea}
                        onMarcarCompletado={marcarCompletado}
                    />
                )}
            </div>
        </DndContext>
    );
};
